/** 后端 API 返回的统一包装格式 */
export interface ApiResponse<T> {
  ok: boolean
  data: T
}

export interface ApiErrorResponse {
  ok: false
  code: string
  message: string
  detail?: string
}

export type NotificationCategory = 'billing' | 'task' | 'system' | 'announcement'

export interface UserNotification {
  id: number
  announcementId: number | null
  category: NotificationCategory
  title: string
  body: string
  actionLabel: string | null
  actionUrl: string | null
  referenceType: string | null
  referenceId: string | null
  metadata: Record<string, unknown> | null
  readAt: string | null
  createdAt: string
}

export interface NotificationInboxData {
  unreadCount: number
  items: UserNotification[]
}

/** Studio */
export interface Studio {
  id: number
  studioKey: string
  displayName: string
  slug: string | null
  status: string
  planTier: string
  role: string
  settings: Record<string, unknown> | null
}

/** API 密钥 */
export interface ApiKey {
  id: number
  accessKey: string
  keyType: string
  status: string
  lastUsedAt: string | null
  expiresAt: string | null
}

/** Project 列表项 */
export interface ProjectListItem {
  id: number
  projectNo: string
  title: string
  type: string
  status: string
  durationMs: number | null
  coverUrl: string | null
  sourceInputType: string | null
  sourceInputUrl: string | null
  primaryLanguage: string | null
  aspectRatio: string | null
  updatedAt: string
  createdAt: string
}

/** Project 详情 */
export interface ProjectDetail {
  id: number
  projectNo: string
  title: string
  description: string | null
  type: string
  status: string
  durationMs: number | null
  outputDurationMs: number | null
  coverUrl: string | null
  sourceInputType: string | null
  sourceInputUrl: string | null
  primaryLanguage: string | null
  targetLanguages: string[] | null
  publishGoal: Record<string, unknown> | null
  aspectRatio: string | null
  width: number | null
  height: number | null
  fps: number | null
  brandTemplateId: number | null
  latestSnapshotId: number | null
  createdAt: string
  updatedAt: string
}

/** 项目快照摘要 */
export interface SnapshotSummary {
  id: number
  snapshotNo: number
  snapshotType: string
  editorSchemaVersion: number | null
  bundleSchemaVersion: number | null
  summaryJson: Record<string, unknown> | null
  createdByUserId: number | null
  createdAt: string
}

/** 项目快照详情 */
export interface SnapshotDetail extends SnapshotSummary {
  snapshotJson: Record<string, unknown> | null
  checksumSha256: string | null
}

/** 分页列表返回 */
export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

/** 创建项目请求 */
export interface CreateProjectInput {
  title: string
  type?: string
  sourceInputType?: 'upload' | 'url' | 'imported' | 'library'
  sourceInputUrl?: string
  primaryLanguage?: string
  targetLanguages?: string[]
  publishGoal?: Record<string, unknown>
  brandTemplateId?: number | null
  aspectRatio?: string
  resolution?: string
  description?: string
}

/** 文件上传结果 */
export interface AssetUploadResult {
  id: number
  publicUrl: string
  fileName: string
  fileSize: number
  mimeType: string
  libraryCategory: string
  assetKind: string
}

/** 素材库列表项 */
export interface Asset {
  id: number
  assetNo: string
  assetKind: string
  libraryCategory: string
  title: string | null
  fileName: string
  publicUrl: string | null
  thumbnailUrl: string | null
  waveformUrl: string | null
  mimeType: string | null
  fileExt: string | null
  fileSize: number | null
  imageWidth: number | null
  imageHeight: number | null
  durationMs: number | null
  fontFamilyName: string | null
  status: string
  tags: string[] | null
  metadata: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

/** 素材库分类 */
export type LibraryCategory = 'image' | 'video' | 'audio' | 'font' | 'subtitle'

/** 分片任务状态 */
export type ClippingJobStatus = 'transcribing' | 'analyzing' | 'completed' | 'failed'

/** 转录句子 */
export interface TranscriptSentence {
  text: string
  startMs: number
  endMs: number
  speakerId: string
}

/** 转录结果 */
export interface TranscriptResult {
  words: Array<{ text: string; startMs: number; endMs: number; sentenceId: number }>
  sentences: TranscriptSentence[]
  durationMs: number
  language: string
}

/** 高光片段候选 */
export interface ClipCandidate {
  startMs: number
  endMs: number
  title: string
  score: number
  reason: string
}

/** 分片任务查询结果 */
export interface ClippingJobResult {
  taskId: string
  projectId: number | null
  status: ClippingJobStatus
  transcript: TranscriptResult | null
  clips: ClipCandidate[] | null
  error: string | null
  /** 持久化的配置参数（从 DB 恢复，用于项目详情页） */
  videoUrl?: string
  captionStyle?: string
  brandTemplateId?: number | null
  brandTemplateName?: string | null
  orientation?: string
  resolution?: string
  language?: string
}

/* ------------------------------------------------------------------ */
/* Variant（项目输出版本）                                             */
/* ------------------------------------------------------------------ */

/** 版本类型 */
export type VariantType =
  | 'clip'
  | 'caption'
  | 'dub'
  | 'translation'
  | 'platform_export'
  | 'digital_human'

/** 版本状态 */
export type VariantStatus = 'draft' | 'generated' | 'review' | 'published' | 'failed'

/** 版本对象 */
export interface Variant {
  id: number
  projectId: number
  variantKey: string
  variantType: VariantType
  title: string
  platformCode: string | null
  aspectRatio: string | null
  locale: string | null
  status: VariantStatus
  sourceAssetId: number | null
  resultAssetId: number | null
  settings: Record<string, unknown> | null
  metrics: Record<string, unknown> | null
  publishedAt: string | null
  createdAt: string
  updatedAt: string
}

/** 从 clip variant 的 settings/metrics 还原出 ClipCandidate */
export function variantToClipCandidate(v: Variant): ClipCandidate {
  const settings = v.settings ?? {}
  const metrics = v.metrics ?? {}
  return {
    startMs: Number(settings.startMs ?? 0),
    endMs: Number(settings.endMs ?? 0),
    title: v.title,
    score: Number(metrics.viralityScore ?? 0),
    reason: String(metrics.scoreReason ?? ''),
  }
}

/* ------------------------------------------------------------------ */
/* ExportRecord（导出记录）                                            */
/* ------------------------------------------------------------------ */

/** 导出记录状态 */
export type ExportStatus = 'created' | 'running' | 'success' | 'failed' | 'canceled'

/** 发布状态 */
export type PublishStatus = 'not_scheduled' | 'scheduled' | 'published' | 'failed'

/** 导出记录 */
export interface ExportRecord {
  id: number
  exportNo: string
  projectId: number
  variantId: number | null
  userId: number
  status: ExportStatus
  publishStatus: PublishStatus
  targetPlatform: string | null
  locale: string | null
  exportPresetCode: string | null
  containerFormat: string | null
  aspectRatio: string | null
  width: number | null
  height: number | null
  durationMs: number | null
  resultFileName: string | null
  resultFileSize: number | null
  resultUrl: string | null
  coverUrl: string | null
  errorMessage: string | null
  extra: Record<string, unknown> | null
  startedAt: string | null
  finishedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface PublicShareLink {
  id: number
  shareKey: string
  studioId: number
  projectId: number
  variantId: number | null
  exportRecordId: number | null
  title: string
  captionText: string | null
  coverUrl: string | null
  durationMs: number | null
  visibility: 'public' | 'unlisted' | 'password'
  status: 'creating' | 'active' | 'expired' | 'revoked' | 'failed'
  expiresAt: string | null
  createdAt: string
  updatedAt: string
}

/* ------------------------------------------------------------------ */
/* Template（品牌模板）                                                */
/* ------------------------------------------------------------------ */

/** 模板作用域 */
export type TemplateScope = 'workbench' | 'brand' | 'caption_style'

/** 品牌模板 */
export interface Template {
  id: number
  templateKey: string
  templateName: string
  templateScope: TemplateScope
  templateType: string
  workbenchType: string
  presetCode: string | null
  width: number | null
  height: number | null
  aspectRatio: string | null
  fps: number | null
  visibility: string
  isDefault: boolean
  coverUrl: string | null
  previewAssetId: number | null
  description: string | null
  status: string
  config: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

/** 模板样式变体 */
export interface TemplateStyleVariant {
  id: number
  templateId: number
  tabKey: string
  styleKey: string
  styleName: string
  previewAssetId: number | null
  sortOrder: number
  isDefault: boolean
  status: string
  config: Record<string, unknown>
  createdAt: string
  updatedAt: string
}
